<template>
	<div
		class="width-full d-flex flex-column relative px-6 pt-28"
		:class="{ 'rounded-t-4': !isXS }"
		style="height: 250px"
	>
		<PButton
			type="text"
			color="reverse"
			class="absolute z-1 right-0 top-0 justify-flex-end mr-4 mt-2"
			@click="$emit('skip')"
		>
			{{ $t('common_skip') }}
		</PButton>
		<Cover
			class="absolute top-0 right-0 left-0"
			:class="{ 'rounded-t-4': !isXS }"
			height="100%"
			:src="type === 'visited' ? visitedCover : wishlistCover"
		/>
		<div class="d-flex flex-column flex-grow-1">
			<div class="relative flex-grow-1">
				<div class="width-full d-flex flex-column absolute header-destinations-wizard">
					<div classflex-grow-="d-flex flex-column width-full gap-10">
						<div class="d-flex flex-column gap-1 text-neutral-25">
							<h3 class="mb-2" :class="{ 'text-title-m': !isXS, 'text-title-s': isXS }">
								{{ $t(title) }}
							</h3>
							<p :class="{ 'text-body-m': !isXS, 'text-body-s': isXS }">
								{{ $t(description) }}
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="relative flex-grow-1">
			<div
				class="absolute width-full bottom-0 d-flex justify-space-around"
				style="transform: translateY(50%)"
			>
				<div v-for="(sticker, i) in stickers" :key="i" class="d-flex pa-1">
					<img :src="sticker" class="width-full" />
				</div>
			</div>
		</div>
	</div>
	<div class="pt-12 px-6">
		<GoogleBrowser
			:types="['(regions)']"
			:is-x-s="isXS"
			:placeholder="String($t(`${placeholder}`))"
			small
			@select="selectPlace"
			@click-result="isLoading = true"
		/>
		<div class="chip-container width-full pt-2" v-if="currentDestinations.length">
			<div class="d-flex flex-wrap gap-2 justify-start height-full width-full">
				<Chip
					v-for="item in currentDestinations"
					:key="item.destinationId"
					:size="isXS ? 's' : 'm'"
					class="font-weight-bold"
					:class="chipClass"
				>
					<div class="d-flex gap-4 align-center">
						<p>
							{{ item.name }}
						</p>

						<button
							class="d-flex align-center bg-neutral-25 rounded-circle pa-1 close-button"
							@click="() => removePlace(item)"
						>
							<Icon icon="close" />
						</button>
					</div>
				</Chip>
				<div v-if="isLoading" class="chip-skeleton">
					<Skeleton :class="isXS ? 'chip--skeleton-s' : 'chip-skeleton-m'" class="rounded-pill" />
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { Destination2 } from '~/models/Destination'
	import { ProfileDestination, ProfileDestinationType } from '~/models/ProfileDestination'
	import wishlistCover from '~/assets/profileWizard/profile-wizard-wishlist.png'
	import visitedCover from '~/assets/profileWizard/profile-wizard-visited.png'
	import { computed, ref, watch } from 'vue'
	import { ProfileService } from '~/passporter-services/profile/service'

	interface Props {
		stickers: any
		title: string
		description: string
		placeholder: string
		isXS: boolean
		travelDestinations?: ProfileDestination[]
		type: ProfileDestinationType
	}

	const props = defineProps<Props>()
	const emit = defineEmits(['updateDestinations', 'next', 'deleteDestination', 'skip'])
	const currentDestinations = ref<ProfileDestination[]>([])
	const isLoading = ref<boolean>(false)

	const chipClass = computed(() =>
		props.type === 'visited'
			? 'bg-primary-25 text-primary-700'
			: 'bg-secondary-25 text-secondary-700'
	)

	watch(
		() => props.travelDestinations,
		() => {
			if (!props.travelDestinations?.length) {
				currentDestinations.value = []
				return
			}

			currentDestinations.value = [...props.travelDestinations]
		},
		{ immediate: true }
	)

	const selectPlace = (destination: Destination2) => {
		const parsed = ProfileService.destinationToProfileDestination(destination, props.type)
		emit('updateDestinations', parsed)
		currentDestinations.value.push(parsed)
		isLoading.value = false
	}

	const removePlace = (destination: ProfileDestination) => {
		emit('deleteDestination', destination)
		currentDestinations.value = currentDestinations.value.filter(
			(item) => item.destinationId !== destination.destinationId
		)
	}
</script>

<style lang="scss" scoped>
	.stickers {
		transform: translateY(25%);
	}
	.chip-container {
		min-height: 150px;
		max-height: 150px;
		overflow-y: auto;
	}

	.close-button {
		&:hover {
			background-color: var(--color-neutral-100);
		}
	}

	.header-destinations-wizard {
		justify-self: center;
	}

	.chip-skeleton-m {
		width: 120px;
		height: 40px;
	}
	.chip--skeleton-s {
		width: 120px;
		height: 32px;
	}
</style>
